import type { QueueKanbanColumn, QueueTone } from '@/components/designSystem/QueueViews/types'
import {
  CONTAINER_STAGES,
  DS_ATTENDANCE_STAGES,
  OPEN_CONTAINER_STAGES
} from '@/constants/ds-attendance-sla.constants'
import type {
  ContainerSlaFilterState,
  ContainerStage
} from '@/constants/ds-attendance-sla.constants'
import { ContainerKind } from '@/constants/ds-attendance.constants'
import type { DsAttendanceGroupBy } from '@/constants/ds-attendance-sla.constants'
import type {
  DSAttendanceContainer,
  DsAttendanceQueueBaseParams,
  DsAttendanceSlaCounters,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'

/**
 * Colunas do Kanban (spec 2026-10-01 §7.2). Cada coluna sabe (a) o recorte que pede ao
 * `by-company` sobre o recorte da tela e (b) onde ler o total no `sla-counters`. O `matches`
 * usa o estado do SERVIDOR (G2-D13): o chip anda no tique, a coluna só na próxima recarga.
 */

export type DsAttendanceKanbanKey = ContainerSlaFilterState | ContainerStage

export interface DsAttendanceKanbanColumn extends QueueKanbanColumn<DSAttendanceContainer> {
  key: DsAttendanceKanbanKey
  /** `null` = a coluna não tem o que buscar neste recorte (total 0, sem requisição). */
  query: (base: DsAttendanceQueueBaseParams) => Partial<ListContainersByCompanyParams> | null
}

const isOpenStage = (stage: ContainerStage): boolean => OPEN_CONTAINER_STAGES.includes(stage)

const slaKeyOf = (row: DSAttendanceContainer): string => {
  const state = row.sla?.state
  if (!state || state === 'NOT_STARTED') return 'NO_SLA'
  return state === 'URGENT' ? 'ATTENTION' : state
}

const K1: Array<{ key: ContainerSlaFilterState; title: string; tone: QueueTone; hint?: string }> = [
  { key: 'OVERDUE', title: 'Vencida', tone: 'crit' },
  { key: 'ATTENTION', title: 'Atenção', tone: 'warn', hint: 'faltam ≤ min(24 h, 25% da janela)' },
  { key: 'ON_TIME', title: 'No prazo', tone: 'info' },
  { key: 'NO_SLA', title: 'Sem SLA', tone: 'neutral', hint: 'política sem prazo para esta etapa' }
]

function slaStateColumns(): DsAttendanceKanbanColumn[] {
  return K1.map((definition) => ({
    ...definition,
    matches: (row) => slaKeyOf(row) === definition.key,
    query: (base) => {
      const open = (base.stage?.length ? base.stage : [...CONTAINER_STAGES]).filter(isOpenStage)
      if (!open.length) return null
      if (base.overdue && definition.key !== 'OVERDUE') return null
      return { stage: open, slaState: [definition.key], closedSince: undefined, orderBy: 'SLA' }
    }
  }))
}

const REQUESTS_HINT = 'Solicitações abertas ficam na aba Solicitações'

function stageColumns(kind: ContainerKind | undefined): DsAttendanceKanbanColumn[] {
  return DS_ATTENDANCE_STAGES.map((stage) => {
    const key = stage.key as ContainerStage
    const hint =
      kind === ContainerKind.ATIVIDADE && key === 'AWAITING_SUBMISSION'
        ? `${stage.hint} · ${REQUESTS_HINT}`
        : stage.hint
    return {
      key,
      title: stage.label,
      tone: stage.tone,
      hint,
      matches: (row: DSAttendanceContainer) => row.stage === key,
      query: (base: DsAttendanceQueueBaseParams) => ({
        stage: [key],
        orderBy: 'SLA' as const,
        closedSince: key === 'CLOSED' ? base.closedSince : undefined
      })
    }
  })
}

export function dsAttendanceKanbanColumns(
  groupBy: DsAttendanceGroupBy,
  options: { kind?: ContainerKind } = {}
): DsAttendanceKanbanColumn[] {
  return groupBy === 'etapa' ? stageColumns(options.kind) : slaStateColumns()
}

const isSlaKey = (key: DsAttendanceKanbanKey): key is ContainerSlaFilterState =>
  !(CONTAINER_STAGES as readonly string[]).includes(key)

/** Total do cabeçalho: o contador do servidor (padrão `columnTotals`), não as linhas carregadas. */
export function dsAttendanceKanbanColumnTotal(
  column: DsAttendanceKanbanColumn,
  counters: DsAttendanceSlaCounters | null,
  base: DsAttendanceQueueBaseParams
): number | undefined {
  if (column.query(base) === null) return 0
  if (!counters) return undefined
  return isSlaKey(column.key) ? counters.byView[column.key] : counters.byStage[column.key]
}
